import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import mark from '../assets/images/dalabtire-mark.png';
import { T, FONT_STACK } from './login.theme';
import { COPY, SUPPORT_CONTACTS, whatsappLink } from './login.copy';
import { AlertIcon, CheckIcon, ChatIcon, LoginKeyframes } from './LoginIcons';
import openExternalLink from './openExternalLink';
import { Field, PrimaryButton, Banner } from './LoginControls';
import WaiterRail from './WaiterRail';
import WaiterPinPad from './WaiterPinPad';
import AddWaiterDialog from './AddWaiterDialog';
import RemoveWaiterDialog from './RemoveWaiterDialog';
import { signedIn, clearNotice } from './loginSlice';
import { authenticate, businessIdOf, authErrorMessage } from './authenticate';
import { readProfiles, removeProfile } from './waiterProfiles';
import { readTillBusinessId, writeTillBusinessId } from './tillStore';

/**
 * The login screen.
 *
 * Two ways in, side by side and both always visible:
 *
 *   left  — username and password, for cashiers, managers and accountants
 *   right — a tile per waiter pinned to this till; tapping one turns the rail
 *           into a numeric pad for their 4-digit PIN
 *
 * The till is a touch screen with no keyboard, so the waiter's whole journey
 * in is two taps and four digits, all on the right-hand side of the glass.
 *
 * Nobody is remembered. Every sign-in, by anybody, starts from an empty form
 * or an empty PIN — nothing about a session is written to this computer.
 *
 * A waiter never gets a tile by claiming to be one. The role is read from the
 * server's reply when the tile is created and again on every unlock.
 *
 * The bottom-left corner stacks three things, from the ground up: the
 * connection dot, the Dalabtire support numbers, and — when a waiter has just
 * been signed out by their receipt printing — the toast that says so.
 */
export default function Login(props) {
  const dispatch = useDispatch();
  const notice = useSelector((state) => state.login.notice);

  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [online, setOnline] = React.useState(navigator.onLine);

  const [profiles, setProfiles] = React.useState(() => readProfiles());
  const [adding, setAdding] = React.useState(false);
  const [unlocking, setUnlocking] = React.useState(null);
  const [removing, setRemoving] = React.useState(null);

  /* ── connection ─────────────────────────────────────────────────────── */

  React.useEffect(() => {
    const sync = () => setOnline(navigator.onLine);
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
    };
  }, []);

  /* ── the print toast clears itself; the two failures do not ─────────── */

  React.useEffect(() => {
    if (notice?.kind !== 'printed') return undefined;
    const timer = window.setTimeout(() => dispatch(clearNotice()), 6000);
    return () => window.clearTimeout(timer);
  }, [notice, dispatch]);

  /* ── sign in ────────────────────────────────────────────────────────── */

  const enterApp = ({ user, token }) => {
    dispatch(signedIn({ user, token }));
    if (props.showHandler) props.showHandler(user);
  };

  const submit = async () => {
    if (busy) return;
    if (!username.trim() || !password) {
      setError(COPY.required);
      return;
    }

    setBusy(true);
    setError('');

    try {
      const { user, token } = await authenticate({
        username: username.trim(),
        password,
      });

      // The till remembers the business of whoever last signed in on it, which
      // is what scopes the waiter tiles on the next boot.
      const businessId = businessIdOf(user);
      if (businessId) writeTillBusinessId(businessId);

      enterApp({ user, token });
    } catch (err) {
      setPassword('');
      setError(authErrorMessage(err));
      setBusy(false);
    }
  };

  /* ── waiter tiles ───────────────────────────────────────────────────── */

  const handleUnlocked = ({ user, token }) => {
    setUnlocking(null);
    setProfiles(readProfiles());
    enterApp({ user, token });
  };

  const confirmRemove = (profile) => {
    setProfiles(removeProfile(readTillBusinessId(), profile.id));
    setRemoving(null);
    setUnlocking(null);
  };

  /* ── notices ────────────────────────────────────────────────────────── */

  const banner =
    notice?.kind === 'rejected'
      ? { icon: <AlertIcon size={15} />, text: COPY.rejected, tone: 'danger' }
      : null;

  return (
    <div
      style={{
        display: 'flex',
        width: '100vw',
        height: '100vh',
        background: T.canvas,
        color: T.ink,
        fontFamily: FONT_STACK,
        overflow: 'hidden',
      }}
    >
      <LoginKeyframes />

      {/* ── sign-in side ───────────────────────────────────────────────── */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 26,
            width: T.cardWidth,
            maxWidth: '100%',
          }}
        >
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}
          >
            <img
              src={mark}
              alt={COPY.brand}
              // The mark is a solid dark square, so it gets a radius here
              // rather than reading as a stray photo dropped on the canvas.
              style={{ width: 52, height: 52, borderRadius: 12, objectFit: 'cover' }}
            />
            <span style={{ fontSize: 19, fontWeight: 700, letterSpacing: -0.2 }}>
              {COPY.brand}
            </span>
          </div>

          {banner ? (
            <div style={{ width: '100%' }}>
              <Banner tone={banner.tone} icon={banner.icon}>
                {banner.text}
              </Banner>
            </div>
          ) : null}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
              width: '100%',
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.radiusPanel,
              padding: '30px 30px 26px',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.2 }}>
                {COPY.signIn}
              </span>
              <span style={{ fontSize: 12.5, color: T.inkSoft, lineHeight: 1.45 }}>
                {COPY.signInSub}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Field
                label={COPY.username}
                name="userName"
                value={username}
                onChange={(value) => {
                  setUsername(value);
                  setError('');
                }}
                placeholder={COPY.usernamePlaceholder}
                disabled={busy}
                invalid={Boolean(error)}
                autoFocus={!username}
              />
              <Field
                label={COPY.password}
                name="password"
                type="password"
                value={password}
                onChange={(value) => {
                  setPassword(value);
                  setError('');
                }}
                placeholder={COPY.passwordPlaceholder}
                disabled={busy}
                invalid={Boolean(error)}
                autoFocus={Boolean(username)}
              />
            </div>

            {error ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: T.danger }}>
                <AlertIcon size={14} />
                <span style={{ fontSize: 12, fontWeight: 600 }}>{error}</span>
              </div>
            ) : null}

            <PrimaryButton type="submit" busy={busy} disabled={!online}>
              {COPY.signIn}
            </PrimaryButton>
          </form>
        </div>

        {/* ── support, one line above the connection dot ───────────────── */}
        <div
          style={{
            position: 'absolute',
            left: 24,
            bottom: 46,
            display: 'flex',
            alignItems: 'center',
            gap: 9,
          }}
        >
          <span style={{ fontSize: 11.5, color: T.inkFaint }}>{COPY.needHelp}</span>

          {SUPPORT_CONTACTS.map((contact, index) => (
            <React.Fragment key={contact.id}>
              {index > 0 ? (
                <span style={{ fontSize: 11.5, color: T.inkGhost }} aria-hidden="true">
                  ·
                </span>
              ) : null}
              {/*
                A button, not an anchor: the till hands its links to the
                operating system rather than following them, so there is no
                href to give. `type="button"` matters — this sits outside the
                form, but a bare <button> still defaults to submit and one day
                somebody will move it inside.
              */}
              <button
                type="button"
                className="dalabtire-support-link"
                onClick={() => openExternalLink(whatsappLink(contact))}
                title={COPY.whatsappHint(contact.display)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  font: 'inherit',
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: T.whatsapp,
                  cursor: 'pointer',
                  // The number is read off the screen as often as it is
                  // tapped, so the digits line up rather than drifting.
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <ChatIcon size={13} />
                {contact.display}
              </button>
            </React.Fragment>
          ))}
        </div>

        <div
          style={{
            position: 'absolute',
            left: 24,
            bottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 7,
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: T.radiusPill,
              background: online ? T.paid : T.danger,
            }}
          />
          <span style={{ fontSize: 11.5, color: online ? T.inkFaint : T.danger }}>
            {online ? COPY.connected : COPY.offline}
          </span>
        </div>

        {notice?.kind === 'printed' ? (
          <div
            style={{
              position: 'absolute',
              left: 24,
              // Above the support line, which is above the connection dot.
              // The three stack from the bottom up and must not overlap.
              bottom: 78,
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '10px 13px',
              background: T.ink,
              borderRadius: T.radiusControl,
              boxShadow: T.toast,
            }}
          >
            <span style={{ display: 'flex', color: '#4ADE80' }}>
              <CheckIcon size={14} />
            </span>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: T.surface }}>
              {COPY.printed(notice.orderNumber, notice.name)}
            </span>
          </div>
        ) : null}
      </div>

      {/* ── waiter rail, top-right — or the PIN pad, in its place ─────── */}
      {unlocking ? (
        <WaiterPinPad
          profile={unlocking}
          onCancel={() => setUnlocking(null)}
          onSignedIn={handleUnlocked}
          onStaleProfile={confirmRemove}
        />
      ) : (
        <WaiterRail
          profiles={profiles}
          onOpen={setUnlocking}
          onAdd={() => setAdding(true)}
          onRemove={setRemoving}
        />
      )}

      {adding ? (
        <AddWaiterDialog
          onClose={() => setAdding(false)}
          onAdded={(next) => setProfiles(next)}
        />
      ) : null}

      {removing ? (
        <RemoveWaiterDialog
          profile={removing}
          onCancel={() => setRemoving(null)}
          onConfirm={confirmRemove}
        />
      ) : null}
    </div>
  );
}
